import React, { SyntheticEvent, useEffect, useRef } from "react";
import Customizable from "../Customizable";
import {
  GroupMember,
  Product,
  SelectedProduct,
} from "~/common/Store/models/ProductListing";
import { ProductListingView } from "..";
import ValidationError from "../../ValidationError";
import RequestAQuoteButton from "./RequestAQuoteButton";
import {
  canBeReplaced,
  doesAnyGroupMembersHasMustPurchaseMoreThanOne,
  ProductBoxType,
} from "../logic";
import ViewReplacementButton from "../ViewReplacementButton";
import QuantityDropdown from "../QuantityDropdown";
import AddToCartButton from "../AddToCartButton";
import HiddenFieldsForCart from "../HiddenFieldsForCart";
import { cls } from "@wss/cls";

interface AdditionalControlProps {
  displayRequiredCustomization: boolean;
  forceCustomQuantity: boolean;
  groupingValue: string;
  groupMemberSelected: boolean | undefined;
  handleAddToCart: (event: SyntheticEvent) => void;
  handleQuantityChange: (value: string) => void;
  handleQuantityIsVisible: () => void;
  hasCustomizableProductsOverride: boolean;
  hasMultipleVGGrouping: boolean;
  incrementCount: number;
  isOnCompareProductsPage: boolean;
  isRequestAQuoteEnabled: boolean | undefined;
  isVirtualItem: boolean;
  itemIsInCart: boolean;
  lastQuantityInterval: number;
  previouslyPurchased: boolean;
  product: Product;
  productBoxType: ProductBoxType;
  productListingView: ProductListingView;
  quantity: string;
  selectedGroupMember: false | "" | GroupMember | undefined;
  selectedProduct: SelectedProduct;
  error?: Readonly<ValidationError>;
  setError: React.Dispatch<
    React.SetStateAction<Readonly<ValidationError> | undefined>
  >;
  showQuantityDropdown: boolean;
  startBuy: number;
}

const canButtonOpenModal = (
  hasAccessories: boolean,
  hasMultipleVGGrouping: boolean,
  isVirtualItem: boolean,
) => {
  return hasAccessories || (isVirtualItem && hasMultipleVGGrouping);
};

const isReplaceableOrItemWithQuantity = (
  itemCanBeReplaced: boolean,
  shouldShowQuantityDropdown: boolean,
  showCartAndQuantityComponent: boolean,
) => {
  return (
    itemCanBeReplaced ||
    shouldShowQuantityDropdown ||
    showCartAndQuantityComponent
  );
};

const shouldShowQuantityComponents = (
  mustPurchaseMoreThanOne: boolean,
  anyGroupMembersHasMustPurchaseMoreThanOne: boolean,
) => {
  return mustPurchaseMoreThanOne || anyGroupMembersHasMustPurchaseMoreThanOne;
};

const shouldUpdateQuantityIsVisible = (
  isCustomizeNowProductBox: boolean,
  isInStock: boolean,
  isRequestAQuoteEnabled: boolean | undefined,
  itemCanBeReplaced: boolean,
  shouldShowQuantityDropdown: boolean,
  showCartAndQuantityComponent: boolean,
) => {
  return !(
    isCustomizeNowProductBox ||
    isRequestAQuoteEnabled ||
    (isInStock &&
      isReplaceableOrItemWithQuantity(
        itemCanBeReplaced,
        shouldShowQuantityDropdown,
        showCartAndQuantityComponent,
      ))
  );
};

interface CustomizationProduct {
  customizationUrl: string;
  isCustomizable: boolean;
  link: string;
}

const getConditionalProductInfo = (
  groupMemberSelected: boolean | undefined,
  product: Product,
  selectedProduct: SelectedProduct,
): CustomizationProduct => {
  const { isCustomizable, link, customizationUrl } = groupMemberSelected
    ? selectedProduct
    : product;

  return {
    isCustomizable,
    link,
    customizationUrl,
  };
};

const shouldUndoBtnContainerStyle = (
  anyGroupMembersHasMustPurchaseMoreThanOne: boolean,
  grouping: boolean,
  groupingValue: string,
  isCustomizeNowProductBox: boolean,
  showQuantityDropdown: boolean,
) => {
  return (
    !isCustomizeNowProductBox &&
    ((grouping &&
      ((groupingValue && showQuantityDropdown) ||
        (!groupingValue && anyGroupMembersHasMustPurchaseMoreThanOne))) ||
      (!grouping && showQuantityDropdown))
  );
};

const AdditionalControl = ({
  displayRequiredCustomization,
  forceCustomQuantity,
  groupingValue,
  groupMemberSelected,
  handleAddToCart,
  handleQuantityChange,
  handleQuantityIsVisible,
  hasCustomizableProductsOverride,
  hasMultipleVGGrouping,
  incrementCount,
  isOnCompareProductsPage,
  isRequestAQuoteEnabled,
  isVirtualItem,
  itemIsInCart,
  lastQuantityInterval,
  previouslyPurchased,
  product,
  productBoxType,
  productListingView,
  quantity,
  selectedGroupMember,
  selectedProduct,
  error,
  setError,
  showQuantityDropdown,
  startBuy,
}: AdditionalControlProps) => {
  const {
    grouping,
    groupingAttributes,
    hasAccessories,
    isReplacementEligible = false,
    itemNumber,
    mustBuy,
    mustPurchaseMoreThanOne,
    requestAQuoteUrl,
  } = product;
  const qtyInputRef = useRef<HTMLInputElement>(null);

  const { isCompareWithProductBox, isCustomizeNowProductBox } = productBoxType;
  const { isInStock, isOutlet } = selectedProduct;
  const { isCustomizable, link, customizationUrl } = getConditionalProductInfo(
    groupMemberSelected,
    product,
    selectedProduct,
  );
  const anyGroupMembersHasMustPurchaseMoreThanOne =
    doesAnyGroupMembersHasMustPurchaseMoreThanOne(groupingAttributes);
  const isVirtual = grouping && isVirtualItem;
  const opensModal = canButtonOpenModal(
    hasAccessories,
    hasMultipleVGGrouping,
    isVirtualItem,
  );
  const showQuantityComponents = shouldShowQuantityComponents(
    mustPurchaseMoreThanOne,
    anyGroupMembersHasMustPurchaseMoreThanOne,
  );
  const shouldShowQuantityDropdown =
    showQuantityDropdown && showQuantityComponents;
  const showCartAndQuantityComponent =
    showQuantityComponents && grouping && !groupingValue;
  const itemCanBeReplaced = canBeReplaced(isOutlet, isReplacementEligible);
  const undoBtnContainerStyle = shouldUndoBtnContainerStyle(
    anyGroupMembersHasMustPurchaseMoreThanOne,
    grouping,
    groupingValue,
    isCustomizeNowProductBox,
    showQuantityDropdown,
  );

  const shouldUpdateQuantityVisibility = shouldUpdateQuantityIsVisible(
    isCustomizeNowProductBox,
    isInStock,
    isRequestAQuoteEnabled,
    itemCanBeReplaced,
    shouldShowQuantityDropdown,
    showCartAndQuantityComponent,
  );

  useEffect(() => {
    if (error) {
      qtyInputRef.current?.focus();
    }
  }, [error]);

  useEffect(() => {
    if (shouldUpdateQuantityVisibility) {
      handleQuantityIsVisible();
    }
  }, [handleQuantityIsVisible, shouldUpdateQuantityVisibility]);

  useEffect(() => {
    if (forceCustomQuantity) {
      qtyInputRef.current?.focus();
    }
  }, [forceCustomQuantity]);

  if (isCustomizeNowProductBox) {
    return (
      <Customizable
        {...{
          undoBtnContainerStyle,
          product,
          productListingView,
          isCompareWithProductBox,
          isOnCompareProductsPage,
          isCustomizable,
          itemNumber,
          hasCustomizableProductsOverride,
          isVirtual,
          hasMultipleVGGrouping,
          groupingValue,
          setError,
          handleAddToCart,
          displayRequiredCustomization,
          link,
          customizationUrl,
        }}
      />
    );
  } else if (isRequestAQuoteEnabled) {
    return <RequestAQuoteButton requestAQuoteUrl={requestAQuoteUrl} />;
  } else if (isInStock) {
    if (itemCanBeReplaced) {
      return (
        <ViewReplacementButton
          {...{
            product,
            isCompareWithProductBox,
            productListingView,
            undoBtnContainerStyle,
          }}
        />
      );
    }

    if (shouldShowQuantityDropdown) {
      return (
        <QuantityDropdown
          {...{
            handleQuantityChange,
            product,
            grouping,
            lastQuantityInterval,
            quantity,
            forceCustomQuantity,
            incrementCount,
            handleAddToCart,
            selectedGroupMember,
            mustPurchaseMoreThanOne,
            startBuy,
            mustBuy,
            isCompareWithProductBox,
            productListingView,
            undoBtnContainerStyle,
          }}
        />
      );
    }

    if (showCartAndQuantityComponent) {
      return (
        <div
          className={cls(
            "cartAndQuantity",
            undoBtnContainerStyle &&
              productListingView === "grid" &&
              "-ml-[30px]",
            isCompareWithProductBox && "text-left",
          )}
        >
          <input
            type="submit"
            className="btn btn-cart btn-small float-none"
            name="addToCartButton"
            value="Add to Cart"
            onClick={handleAddToCart}
            data-testid="itemAddCart"
            data-action="addToCart"
          />
        </div>
      );
    }

    return (
      <>
        <HiddenFieldsForCart {...{ product }} />
        <div
          className={cls(
            "cartAndQuantity",
            undoBtnContainerStyle && "-ml-[30px]",
            isCompareWithProductBox && "text-left",
          )}
        >
          <AddToCartButton
            ref={qtyInputRef}
            {...{
              isOutlet,
              itemIsInCart,
              handleAddToCart,
              handleQuantityChange,
              quantity,
              productListingView,
              previouslyPurchased,
              opensModal,
              itemNumber,
            }}
          />
        </div>
      </>
    );
  }

  return null;
};

export default AdditionalControl;
